import {
  Box,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Pill } from '../../../../components/shared/Pill';
import { TableLoading } from '../../../../components/forms/TableLoading';
import { EmptyRows } from '../../../../components/forms/EmptyRows';
import { EmptyTable } from '../../../../components/shared/EmptyTable';
import ModuleItemRow from './ModuleItemRow';
import { ModuleResponse } from '../../../../app/services/api/generated';

type Props = {
  list: ModuleResponse[];
  isLoading: boolean;
  totalCount: number;
  onClear: () => void;
  onCreateNew: () => void;
};

const ModulesList: FC<Props> = ({
  list,
  isLoading,
  totalCount,
  onClear,
  onCreateNew,
}) => {
  const { t } = useTranslation('admin');

  if (totalCount === 0 && !isLoading) {
    return (
      <EmptyTable
        bodyRowHeight={40}
        headerHeight={45}
        text={t('modules.title')}
        searchTerm={''}
        onClear={onClear}
        onAdd={onCreateNew}
        addItemText={t('modules.createNew')}
        onAddIsCreating={false}
      />
    );
  }

  return (
    <>
      <Box
        sx={{
          p: 5,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          width: '100%',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography
            variant="textMD"
            fontWeight={600}>
            {t('modules.title')}
          </Typography>
          {(totalCount ?? 0) > 0 && <Pill text={`${totalCount} ${t('modules.title')}`} />}
        </Box>
      </Box>

      {isLoading ? (
        <TableLoading
          headerHeight={45}
          bodyRowHeight={40}
        />
      ) : (
        <TableContainer>
          <Table sx={{ borderBottomLeftRadius: '12px', borderBottomRightRadius: '12px' }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>
                  {t('modules.name')}
                </TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>
                  {t('modules.description')}
                </TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>
                  {t('modules.system')}
                </TableCell>
                <TableCell
                  align="center"
                  sx={{ whiteSpace: 'nowrap' }}>
                  {t('modules.premium')}
                </TableCell>
                <TableCell
                  align="center"
                  sx={{ whiteSpace: 'nowrap' }}>
                  {t('modules.permissionsCount')}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {list.length > 0 ? (
                <>
                  {list.map((module) => (
                    <ModuleItemRow
                      key={module.id}
                      module={module}
                    />
                  ))}
                  <EmptyRows
                    numberOfCurrentRows={list.length}
                    height={40}
                  />
                </>
              ) : (
                <TableRow>
                  <TableCell
                    colSpan={5}
                    sx={{ textAlign: 'center', py: 4 }}
                  >
                    {t('modules.noModulesFound')}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </>
  );
};

export default ModulesList;
